import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Grid, makeStyles, Paper } from '@material-ui/core';
import { AddBox } from '@material-ui/icons';
import { useRouteMatch } from 'react-router';

import { ActionsBox, ContentContainer, DialogWrap, MapWrapper, TitleContainer } from 'src/components';
import { LoadingBox } from 'src/scenes/Project/scenes/Dashboard/components';
import { MapCreateDialog, ProcessGeoInfoDialog } from 'src/scenes/Project/scenes/Analytics/components';
import { buildLeafletLayer } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { useProjectApi } from 'src/services';
import { getLeafletLegend } from 'src/components/map/mapHelper';
import { InterestAreasLegend, ProcessInterestAreasDialog } from 'src/scenes/Project/scenes/InterestAreas/components';
import { useFetchStatus } from 'src/hooks';


const mapOptions = {
  center: [ -32.48431324745671, -71.25296320202341 ],
  zoomControl: true,
  dragging: true,
  zoom: 16,
  doubleClickZoom: false,
  attributionControl: false,
};

const useStyles = makeStyles(theme => ({
  map: {
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
}));

const interestAreaMapHeight = 70;

const heightVH = n => document.documentElement.clientHeight * (n / 100);

const InterestAreasContainer = ({ interestArea, project }) => {
  const classes = useStyles();
  const match = useRouteMatch();
  const projectApi = useProjectApi();

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;
  const { data: interestAreas, fetchStatus: interestAreaFetchStatus, actions } = interestArea;
  const { addInterestArea, deleteInterestArea } = actions;
  const [ leafletLegend, setLeafletLegend ] = useState(null);
  const [ analyticsInProgress, setAnalyticsInProgress ] = useState({});

  const [ dialog, setDialog ] = useState({ isOpen: false, data: {}, type: '' });

  const closeDialog = () => setDialog({ isOpen: false, type: '' });
  const openCreateMapDialog = () => setDialog({ isOpen: true, type: 'create-map' });
  const openProcessAreaDialog = () => setDialog({ isOpen: true, type: 'process-area' });

  const handleLeafletLegend = layers => setLeafletLegend(prevLegend => {
    prevLegend?.remove();
    return buildLegend(layers);
  });

  const getDialogType = () => {
    switch (dialog.type) {
      case 'create-map': {
        return <MapCreateDialog actions={{ saveAnalyticMap: addInterestArea(handleLeafletLegend), closeDialog }} />;
      }
      case 'process-area': {
        return <ProcessInterestAreasDialog
          actions={{ closeDialog, processInterestArea: analyticTypeIds => processInterestArea(analyticTypeIds) }}
          analyticsInProgress={analyticsInProgress}
        />;
      }
      case 'geo-info': {
        return <ProcessGeoInfoDialog actions={{ closeDialog }} />;
      }
      default:
        break;
    }
  };

  const buildLegend = layers => getLeafletLegend(() => <InterestAreasLegend
    deleteInterestArea={deleteInterestArea(handleLeafletLegend)}
    layers={layers}
  />);

  const { handleConfirm: fetchAnalyticsInProgress } = useFetchStatus({
    confirmFn: async () => {
      const analyticsInProgress = await projectApi.getInProgressAnalytics(projectId);
      setAnalyticsInProgress(analyticsInProgress);
    },
  });

  useEffect(() => {
    fetchAnalyticsInProgress();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLeafletLegend(buildLegend(interestAreas));
  }, [ interestAreas ]); // eslint-disable-line react-hooks/exhaustive-deps

  const { isLoading: isLoadingInterestArea, isSuccess: isSuccessInterestArea } = interestAreaFetchStatus;
  const hasInterestAreas = interestAreas?.length > 0;
  const leafletLayers = interestAreas.map(amd => buildLeafletLayer({
    features: amd.geojson,
    id: amd.id,
    name: amd.name,
  }));


  const processMapper = {
    'spectral-indices': () => projectApi.processInterestArea({ projectId }),
    'pliscoff': () => projectApi.processPrefeasibilityLayer({ projectId, layerId: 'pliscoff' }),
    'protected-areas': () => projectApi.processProtectedAndPriorityZones(projectId),
    'landcover-stats': () => projectApi.processLandcoverStats(projectId),
  };

  const processInterestArea = async analyticTypeIds => {
    analyticTypeIds.map(typeId => processMapper[typeId]());
    setAnalyticsInProgress(prev => {
      const analyticsInProgress = analyticTypeIds.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
      return ({ ...prev, ...analyticsInProgress });
    });
  };

  const hasSomeInProgress = Object.values(analyticsInProgress).some(Boolean);

  return (
    <Grid container>
      <TitleContainer
        maxWidth='xl'
        project={project}
        breadcrumbs={[
          'projects', 'project', 'sites', 'site', { name: 'Áreas de interés' },
        ]}
      >
        Áreas de interés
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        <ActionsBox mt={2} mb={1} display="flex" alignItems="flex-end">
          <Box flexGrow={1} display="flex" height="30px">
            <Button
              disabled={isLoadingInterestArea}
              size="small"
              color="primary"
              startIcon={<AddBox />}
              variant="contained"
              onClick={openCreateMapDialog}
            >
              Agregar área de interés
            </Button>
          </Box>
          {hasInterestAreas &&
            <Box display="flex" height="30px">
              <Button
                size="small"
                color="primary"
                variant={hasSomeInProgress ? 'outlined' : 'contained'}
                onClick={openProcessAreaDialog}
              >
                <Box display="flex" alignItems="center">
                  {hasSomeInProgress && <Box display="flex" mr={1}><CircularProgress size={16} thickness={4.8} /></Box>}
                  Procesar área de interés
                </Box>
              </Button>
            </Box>
          }
        </ActionsBox>
        <Paper>
          <Box p={1}>
            {isLoadingInterestArea && <LoadingBox label="área de interes" />}
            {isSuccessInterestArea &&
              <MapWrapper
                id="interest-area-map"
                className={classes.map}
                options={mapOptions}
                height={isLoadingInterestArea ? 0 : heightVH(interestAreaMapHeight)}
                layers={leafletLayers}
                legend={leafletLegend}
                fitBoundsOnUpdate
              />
            }
          </Box>
        </Paper>
      </ContentContainer>
      <DialogWrap maxWidth={dialog.type === 'process-area' ? 'sm' : 'lg'} fullWidth onClose={closeDialog} open={dialog.isOpen}>
        {getDialogType()}
      </DialogWrap>
    </Grid>
  );
};

InterestAreasContainer.propTypes = {
  interestArea: PropTypes.shape({
    data: PropTypes.array.isRequired,
    fetchStatus: PropTypes.object,
    actions: PropTypes.shape({
      addInterestArea: PropTypes.func,
      deleteInterestArea: PropTypes.func,
    }),
  }).isRequired,
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export { InterestAreasContainer };
